import React, { useState, DragEvent } from 'react';
import { Clock, Save, X, Plus, List, Pencil, Check, ChevronDown, Edit3, Ungroup } from 'lucide-react';
import CollapsibleSection from '../../../shared/components/CollapsibleSection';
import TimeElementCard from './TimeElementCard';
import utilityService from '../../../shared/utils/utilityService';
import { useTime } from '../../../shared/context/TimeContext';
import { useExpandedGroups } from '../hooks/useExpandedGroups';
import { topLevelElements, childrenOf, isGroup, flattenLeafElements, DropPosition } from '../utils/grouping';
import {
  parseTimeToSeconds,
  formatTo12Hour,
  calculateRemainingSeconds,
  calculateExampleTimes,
  getUrgencyLevel,
  calculateElementDuration,
  Element,
  ElementInput,
} from '@thekwallace/backtime-shared';

type ElementState = 'expired' | 'active' | 'upcoming';
type UrgencyLevel = 'imminent' | 'soon' | 'moderate' | 'distant';

interface EditedData {
  description?: string;
  duration?: number;
  anchorOffset?: number | null;
}

interface HeaderInfo {
  active: { description: string } | null;
  upcoming: { description: string; startTime: number } | null;
  upcomingCountdown: number | null;
}

interface TimeElementListProps {
  elements: Element[];
  selectedStartTime: string | null;
  occurrenceStartAbsolute?: number | null;
  timeOffset?: number;
  onAddElement: (element: Partial<ElementInput>) => void;
  onUpdateElement: (elementId: string, updates: Partial<ElementInput>) => void;
  onDeleteElement: (elementId: string) => void;
  onReorderElements: (fromIndex: number, toIndex: number) => void;
  onMoveElement?: (draggedId: string, targetId: string, position: DropPosition, groupName?: string) => void;
  onUngroupElement?: (groupId: string) => void;
  timelineId?: string | null;
  noUpcomingOccurrences?: boolean;
  isOccurrenceComplete?: boolean;
  dayName?: string | null;
  readOnly?: boolean;
}

const TimeElementList: React.FC<TimeElementListProps> = ({
  elements,
  selectedStartTime,
  occurrenceStartAbsolute = null,
  timeOffset = 0,
  onAddElement,
  onUpdateElement,
  onDeleteElement,
  onMoveElement,
  onUngroupElement,
  timelineId = null,
  noUpcomingOccurrences = false,
  isOccurrenceComplete = false,
  readOnly = false
}) => {
  const [editingElementId, setEditingElementId] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<EditedData>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newElementData, setNewElementData] = useState<EditedData>({ description: '', duration: 300 });
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: DropPosition } | null>(null);

  const { getTimeWithOffset } = useTime();
  const currentTimeSeconds = getTimeWithOffset(timeOffset);
  const { isExpanded, toggle } = useExpandedGroups(timelineId);

  const occurrenceStartSeconds = occurrenceStartAbsolute !== null
    ? occurrenceStartAbsolute
    : (!selectedStartTime || selectedStartTime.trim() === '')
      ? 0
      : parseTimeToSeconds(selectedStartTime);

  const currentForCalc = occurrenceStartAbsolute !== null
    ? currentTimeSeconds
    : ((currentTimeSeconds % 86400) + 86400) % 86400;

  const sortedElements = [...elements].sort((a, b) => a.sortOrder - b.sortOrder);

  // Timing must use TREE order (groups expanded in place), not raw global sortOrder —
  // sortOrder is sibling-scoped after grouping. Re-index the flattened leaves so the
  // chained start-time math sequences them correctly.
  const timingElements = flattenLeafElements(elements);

  const { elements: allDisplayElements, elementsById } = utilityService.getAllElements(
    currentForCalc,
    occurrenceStartSeconds,
    timingElements
  );
  const nextElementId = utilityService.findNextElementId(allDisplayElements);

  const showActualTimes = Boolean(selectedStartTime && selectedStartTime.trim() !== '');
  const showCountdowns = !noUpcomingOccurrences && !isOccurrenceComplete && showActualTimes;

  const getElementState = (elementId: string): ElementState => {
    if (noUpcomingOccurrences) return 'upcoming';
    const element = elementsById[elementId];
    if (!element) return 'upcoming';
    return utilityService.getElementState(element, currentForCalc);
  };

  const getUrgency = (elementId: string): UrgencyLevel => {
    if (noUpcomingOccurrences) return 'distant';
    const element = elementsById[elementId];
    if (!element || element.countdown <= 0) return 'moderate';
    return getUrgencyLevel(element.countdown) as UrgencyLevel;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatStartTime = (element: Element): string => {
    const displayElement = elementsById[element.id];
    return noUpcomingOccurrences && displayElement
      ? calculateExampleTimes(displayElement.startTime - occurrenceStartSeconds)
      : showActualTimes && displayElement
        ? formatTo12Hour(displayElement.startTime, true)
        : `+${formatDuration(displayElement?.startTime ?? 0)}`;
  };

  // Editing
  const handleStartEdit = (element: Element) => {
    setEditingElementId(element.id);
    setEditedData({ description: element.description, duration: element.duration ?? 0, anchorOffset: element.anchorOffset });
  };
  const handleSaveEdit = () => {
    if (!editingElementId) return;
    onUpdateElement(editingElementId, { description: editedData.description, duration: editedData.duration, anchorOffset: editedData.anchorOffset });
    setEditingElementId(null);
    setEditedData({});
  };
  const handleCancelEdit = () => { setEditingElementId(null); setEditedData({}); };

  // Edit mode reveals row reordering, the Add form, and per-row (⋯) actions.
  // Leaving edit mode dismisses any open inline form so operate mode stays clean.
  const toggleEditMode = () => {
    setEditMode((on) => {
      if (on) { setShowAddForm(false); setEditingElementId(null); setEditedData({}); }
      return !on;
    });
  };

  // Groups only have a name to edit (duration is calculated, no anchor) — save just the description.
  const handleSaveGroupRename = () => {
    if (!editingElementId) return;
    onUpdateElement(editingElementId, { description: editedData.description });
    setEditingElementId(null);
    setEditedData({});
  };

  const handleAddNewElement = () => {
    onAddElement({
      description: newElementData.description || 'New Element',
      duration: newElementData.duration || 300,
      anchorOffset: newElementData.anchorOffset ?? null
    });
    setNewElementData({ description: '', duration: 300 });
    setShowAddForm(false);
  };

  // Drag-and-drop: thirds (before / into / after). `into` only offered for top-level targets.
  const handleDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>, id: string, allowInto: boolean) => {
    e.preventDefault();
    if (id === draggedId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let position: DropPosition;
    if (allowInto && y > rect.height / 3 && y < (rect.height * 2) / 3) position = 'into';
    else position = y < rect.height / 2 ? 'before' : 'after';
    setDropTarget({ id, position });
  };
  const handleDragEnd = () => {
    if (draggedId && dropTarget && draggedId !== dropTarget.id && onMoveElement) {
      if (dropTarget.position === 'into') {
        const target = elements.find((el) => el.id === dropTarget.id);
        const targetIsGroup = target ? isGroup(elements, target) : false;
        if (targetIsGroup) {
          onMoveElement(draggedId, dropTarget.id, 'into');
        } else {
          const name = window.prompt('Name this group:', 'Group');
          if (name !== null) onMoveElement(draggedId, dropTarget.id, 'into', name.trim() || 'Group');
        }
      } else {
        onMoveElement(draggedId, dropTarget.id, dropTarget.position);
      }
    }
    setDraggedId(null);
    setDropTarget(null);
  };

  const dropClasses = (id: string): string => {
    if (dropTarget?.id !== id) return '';
    if (dropTarget.position === 'before') return 'border-t-2 border-blue-500';
    if (dropTarget.position === 'after') return 'border-b-2 border-blue-500';
    return 'ring-2 ring-blue-500 rounded-lg'; // into
  };

  const getHeaderInfo = (): HeaderInfo | null => {
    if (sortedElements.length === 0) return null;
    const nextElement = utilityService.getNextTimeElement(currentForCalc, occurrenceStartSeconds, timingElements);
    let activeElement: { description: string } | null = null;
    for (const el of allDisplayElements) {
      if (utilityService.getElementState(el, currentForCalc) === 'active') { activeElement = { description: el.description }; break; }
    }
    let upcomingCountdown: number | null = null;
    if (nextElement) {
      const remaining = calculateRemainingSeconds(currentForCalc, nextElement.startTime);
      if (remaining > 0) upcomingCountdown = remaining;
    }
    return {
      active: activeElement,
      upcoming: nextElement && upcomingCountdown && upcomingCountdown > 0 ? nextElement : null,
      upcomingCountdown
    };
  };

  // ---- Render helpers ----

  const renderEditForm = (element: Element) => (
    <div key={element.id} className="p-4 rounded-lg bg-white dark:bg-gray-800 border border-blue-500">
      <div className="space-y-4">
        <input
          type="text"
          placeholder="Description"
          value={editedData.description || ''}
          onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
          className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Duration (MM:SS)</label>
            <div className="flex items-center gap-1">
              <input type="number" min="0" value={Math.floor((editedData.duration || 0) / 60)}
                onChange={(e) => { const minutes = parseInt(e.target.value) || 0; const cur = (editedData.duration || 0) % 60; setEditedData({ ...editedData, duration: minutes * 60 + cur }); }}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
              <span className="text-gray-400 text-lg font-bold">:</span>
              <input type="number" min="0" max="59" value={(editedData.duration || 0) % 60}
                onChange={(e) => { const seconds = Math.min(59, Math.max(0, parseInt(e.target.value) || 0)); const curM = Math.floor((editedData.duration || 0) / 60); setEditedData({ ...editedData, duration: curM * 60 + seconds }); }}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Anchor offset</label>
            <div className="flex space-x-2">
              <input type="number" placeholder="Min"
                value={editedData.anchorOffset !== null && editedData.anchorOffset !== undefined ? Math.floor(editedData.anchorOffset / 60) : ''}
                onChange={(e) => { const v = e.target.value; if (v === '' && ((editedData.anchorOffset ?? 0) % 60) === 0) setEditedData({ ...editedData, anchorOffset: null }); else { const m = parseInt(v) || 0; const s = (editedData.anchorOffset ?? 0) % 60; setEditedData({ ...editedData, anchorOffset: m * 60 + s }); } }}
                className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
              <input type="number" min="0" max="59" placeholder="Sec"
                value={editedData.anchorOffset !== null && editedData.anchorOffset !== undefined ? (editedData.anchorOffset % 60) : ''}
                onChange={(e) => { const v = e.target.value; if (v === '' && Math.floor((editedData.anchorOffset ?? 0) / 60) === 0) setEditedData({ ...editedData, anchorOffset: null }); else { const s = Math.min(59, Math.max(0, parseInt(v) || 0)); const m = Math.floor((editedData.anchorOffset ?? 0) / 60); setEditedData({ ...editedData, anchorOffset: m * 60 + s }); } }}
                className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <button onClick={handleSaveEdit} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center justify-center">
            <Save className="w-4 h-4 mr-2" /> Save
          </button>
          <button onClick={handleCancelEdit} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // A single leaf element row (draggable). `isChild` rows can't be a group target.
  const renderCard = (element: Element, isChild: boolean) => {
    if (editingElementId === element.id) return renderEditForm(element);
    const displayElement = elementsById[element.id];
    return (
      <div
        key={element.id}
        draggable={!readOnly && editMode}
        onDragStart={(e) => handleDragStart(e, element.id)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => handleDragOver(e, element.id, !isChild)}
        className={`transition-all ${draggedId === element.id ? 'opacity-50' : ''} ${dropClasses(element.id)}`}
      >
        <TimeElementCard
          element={{
            id: element.id,
            description: element.description,
            duration: element.duration ?? 0,
            startTime: displayElement?.startTime ?? 0,
            endTime: displayElement?.endTime ?? 0,
            countdown: displayElement?.countdown ?? 0,
          }}
          state={getElementState(element.id)}
          isNextUp={element.id === nextElementId}
          urgency={getUrgency(element.id)}
          showCountdown={showCountdowns}
          showActualTimes={showActualTimes}
          formattedStartTime={formatStartTime(element)}
          currentTime={currentForCalc}
          onEdit={!readOnly ? () => handleStartEdit(element) : undefined}
          onDelete={!readOnly ? () => onDeleteElement(element.id) : undefined}
          draggable={!readOnly && editMode}
          editMode={editMode}
          offsetInfo={element.anchorOffset !== null
            ? `Anchored at +${Math.floor(element.anchorOffset / 60)}m${element.anchorOffset % 60 > 0 ? ` ${element.anchorOffset % 60}s` : ''} from occurrence start`
            : undefined}
        />
      </div>
    );
  };

  // A group renders like a normal element row (so collapsed it reads as one element),
  // with a disclosure chevron + ungroup action, and is draggable as a whole.
  const renderGroup = (group: Element) => {
    // Rename form (groups only edit their name)
    if (editingElementId === group.id) {
      return (
        <div key={group.id} className="p-4 rounded-lg bg-white dark:bg-gray-800 border border-blue-500">
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Group name"
              value={editedData.description || ''}
              onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex space-x-2">
              <button onClick={handleSaveGroupRename} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center justify-center">
                <Save className="w-4 h-4 mr-2" /> Save
              </button>
              <button onClick={handleCancelEdit} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    const kids = childrenOf(elements, group.id);
    const kidDisplays = kids.map((k) => elementsById[k.id]).filter(Boolean) as Array<{ startTime: number; endTime: number }>;
    const groupDuration = calculateElementDuration({ ...group, children: kids });
    const expanded = isExpanded(group.id);
    const startTime = kidDisplays.length ? Math.min(...kidDisplays.map((d) => d.startTime)) : 0;
    const endTime = kidDisplays.length ? Math.max(...kidDisplays.map((d) => d.endTime)) : startTime;
    const groupCountdown = Math.max(0, calculateRemainingSeconds(currentForCalc, startTime));
    const groupState: ElementState = noUpcomingOccurrences
      ? 'upcoming'
      : currentForCalc < startTime ? 'upcoming' : currentForCalc < endTime ? 'active' : 'expired';
    // Mirror the leaf getUrgency() so a collapsed group colors consistently with its children:
    // no upcoming occurrence => distant (gray), not the moderate (green) default.
    const groupUrgency: UrgencyLevel = noUpcomingOccurrences
      ? 'distant'
      : groupCountdown <= 0
        ? 'moderate'
        : (getUrgencyLevel(groupCountdown) as UrgencyLevel);
    const groupIsNextUp = kids.some((k) => k.id === nextElementId);
    const groupFormattedStart = noUpcomingOccurrences
      ? calculateExampleTimes(startTime - occurrenceStartSeconds)
      : showActualTimes ? formatTo12Hour(startTime, true) : `+${formatDuration(startTime)}`;

    // Collapsed: the group reads as a single time-element card (group-span timing/state),
    // with a right-facing disclosure chevron. Click the chevron to expand.
    if (!expanded) {
      return (
        <div key={group.id}>
          <div
            draggable={!readOnly && editMode}
            onDragStart={(e) => handleDragStart(e, group.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, group.id, true)}
            className={`transition-all ${draggedId === group.id ? 'opacity-50' : ''} ${dropClasses(group.id)}`}
          >
            <TimeElementCard
              element={{ id: group.id, description: group.description, duration: groupDuration, startTime, endTime, countdown: groupCountdown }}
              state={groupState}
              isNextUp={groupIsNextUp}
              urgency={groupUrgency}
              showCountdown={showCountdowns}
              showActualTimes={showActualTimes}
              formattedStartTime={groupFormattedStart}
              currentTime={currentForCalc}
              draggable={!readOnly && editMode}
              editMode={editMode}
              isGroup
              expanded={false}
              onToggleExpand={() => toggle(group.id)}
              onEdit={!readOnly ? () => handleStartEdit(group) : undefined}
              onUngroup={!readOnly && onUngroupElement ? () => onUngroupElement(group.id) : undefined}
              itemCount={kids.length}
            />
          </div>
        </div>
      );
    }

    // Expanded: drop the summary card — show just a down-chevron + connecting line and the
    // children's own cards. Clicking the chevron (or the group label) collapses back to the card.
    return (
      <div key={group.id}>
        <div className="flex items-center gap-1.5 mb-2 pl-1">
          <button
            onClick={() => toggle(group.id)}
            aria-label="Collapse group"
            aria-expanded={true}
            className="group flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{group.description}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">· {formatDuration(groupDuration)}</span>
          </button>
          {editMode && !readOnly && (
            <div className="flex items-center gap-0.5">
              <button onClick={() => handleStartEdit(group)} title="Rename group" aria-label="Rename group"
                className="p-1.5 text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-all">
                <Edit3 className="w-4 h-4" />
              </button>
              {onUngroupElement && (
                <button onClick={() => onUngroupElement(group.id)} title="Ungroup" aria-label="Ungroup"
                  className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all">
                  <Ungroup className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        {/* Children with a connecting line down the left edge */}
        <div className="ml-2 sm:ml-3 pl-3 border-l-2 border-gray-300 dark:border-gray-600 space-y-2">
          {kids.map((child) => renderCard(child, true))}
        </div>
      </div>
    );
  };

  const tree = topLevelElements(elements);

  return (
    <CollapsibleSection
      title="Schedule"
      icon={<List className="w-6 h-6 text-blue-400" />}
      defaultOpen={true}
      hideHeaderContentWhenOpen={true}
      headerContent={
        sortedElements.length > 0 ? (() => {
          if (showActualTimes && isOccurrenceComplete) {
            return <div className="flex items-center gap-2 text-xs sm:text-sm"><span className="text-gray-400">Occurrence complete</span></div>;
          }
          if (showCountdowns) {
            const headerInfo = getHeaderInfo();
            if (!headerInfo) return null;
            return (
              <div className="flex flex-col gap-1">
                {headerInfo.active && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <span className="text-gray-900 dark:text-white font-semibold">{headerInfo.active.description}</span>
                    <span className="text-green-500 dark:text-green-400">is happening now.</span>
                  </div>
                )}
                {headerInfo.upcoming && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <span className="text-gray-900 dark:text-white font-semibold">{headerInfo.upcoming.description}</span>
                    <span className="text-gray-500 dark:text-gray-400">is up next in:</span>
                    <span className="text-gray-900 dark:text-white font-mono font-bold">{utilityService.formatCountdown(headerInfo.upcomingCountdown ?? 0)}</span>
                  </div>
                )}
              </div>
            );
          }
          if (noUpcomingOccurrences && allDisplayElements.length > 0) {
            const firstElement = allDisplayElements[0];
            const exampleTime = calculateExampleTimes(firstElement.startTime - occurrenceStartSeconds);
            return (
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="text-gray-900 dark:text-white font-semibold">{firstElement.description}</span>
                <span className="text-gray-500 dark:text-gray-400">starts at</span>
                <span className="text-gray-900 dark:text-white font-mono">{exampleTime}</span>
              </div>
            );
          }
          return <div className="flex items-center gap-2 text-xs sm:text-sm"><span className="text-gray-500 dark:text-gray-400">{sortedElements.length} element{sortedElements.length !== 1 ? 's' : ''}</span></div>;
        })() : null
      }
      headerRight={
        !readOnly && (
          <div className="flex items-center gap-2">
            {/* Add Element — only available while editing */}
            {editMode && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowAddForm(!showAddForm); }}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 text-white rounded-lg transition-all duration-200 transform hover:scale-105 ${showAddForm ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {showAddForm ? (
                  <><X className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium">Cancel</span></>
                ) : (
                  <><Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium hidden xs:inline">Add Element</span><span className="text-xs sm:text-sm font-medium xs:hidden">Add</span></>
                )}
              </button>
            )}
            {/* Edit / Done toggle: switches between operate and edit modes */}
            <button
              onClick={(e) => { e.stopPropagation(); toggleEditMode(); }}
              aria-pressed={editMode}
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 ${editMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'}`}
            >
              {editMode ? (
                <><Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium">Done</span></>
              ) : (
                <><Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium">Edit</span></>
              )}
            </button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {/* Add Element Form */}
        {!readOnly && showAddForm && (
          <div className="bg-gray-200/30 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-300 dark:border-gray-600">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-500 dark:text-green-400" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Add Element</h3>
              </div>
            </div>
            <div className="space-y-4">
              <input type="text" placeholder="Description (e.g., Worship, Message)" value={newElementData.description || ''}
                onChange={(e) => setNewElementData({ ...newElementData, description: e.target.value })}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Duration (MM:SS)</label>
                  <div className="flex items-center gap-1">
                    <input type="number" min="0" value={Math.floor((newElementData.duration || 0) / 60)}
                      onChange={(e) => { const m = parseInt(e.target.value) || 0; const s = (newElementData.duration || 0) % 60; setNewElementData({ ...newElementData, duration: m * 60 + s }); }}
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                    <span className="text-gray-400 text-lg font-bold">:</span>
                    <input type="number" min="0" max="59" value={(newElementData.duration || 0) % 60}
                      onChange={(e) => { const s = Math.min(59, Math.max(0, parseInt(e.target.value) || 0)); const m = Math.floor((newElementData.duration || 0) / 60); setNewElementData({ ...newElementData, duration: m * 60 + s }); }}
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Anchor offset (optional)</label>
                  <div className="flex space-x-2">
                    <input type="number" placeholder="Min"
                      value={newElementData.anchorOffset !== null && newElementData.anchorOffset !== undefined ? Math.floor(newElementData.anchorOffset / 60) : ''}
                      onChange={(e) => { const v = e.target.value; if (v === '' && ((newElementData.anchorOffset ?? 0) % 60) === 0) setNewElementData({ ...newElementData, anchorOffset: null }); else { const m = parseInt(v) || 0; const s = (newElementData.anchorOffset ?? 0) % 60; setNewElementData({ ...newElementData, anchorOffset: m * 60 + s }); } }}
                      className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                    <input type="number" min="0" max="59" placeholder="Sec"
                      value={newElementData.anchorOffset !== null && newElementData.anchorOffset !== undefined ? (newElementData.anchorOffset % 60) : ''}
                      onChange={(e) => { const v = e.target.value; if (v === '' && Math.floor((newElementData.anchorOffset ?? 0) / 60) === 0) setNewElementData({ ...newElementData, anchorOffset: null }); else { const s = Math.min(59, Math.max(0, parseInt(v) || 0)); const m = Math.floor((newElementData.anchorOffset ?? 0) / 60); setNewElementData({ ...newElementData, anchorOffset: m * 60 + s }); } }}
                      className="flex-1 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Leave anchor offset empty for chained elements (start after previous ends). Set a value to anchor at a specific time from occurrence start.</p>
              <button onClick={handleAddNewElement} className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all flex items-center justify-center">
                <Plus className="w-4 h-4 mr-2" /> Add Element
              </button>
            </div>
          </div>
        )}

        {/* Tip (edit mode only) */}
        {!readOnly && editMode && sortedElements.length > 1 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">Tip: drag an element onto another to group them; drag to the top/bottom edge to reorder.</p>
        )}

        {/* Elements List (tree) */}
        {sortedElements.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">No elements yet</div>
            <div className="text-gray-500 text-sm">Add your first element using the button above</div>
          </div>
        ) : (
          <div className="space-y-2">
            {tree.map((element) => (isGroup(elements, element) ? renderGroup(element) : renderCard(element, false)))}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default TimeElementList;
